import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import * as walletService from '../services/walletService';

/**
 * Componente autónomo para mostrar pedidos pendientes de conciliación.
 * No requiere props - maneja su propio estado y carga de datos.
 * Puede ser usado en cualquier parte de la aplicación.
 */
const PendingReconciliationSection = () => {
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    // Función para cargar datos
    const fetchData = async (newPage = 1, newSearch = '') => {
        try {
            setLoading(true);
            const res = await walletService.getPendingReconciliation({
                page: newPage,
                limit: 10,
                search: newSearch
            });
            setData(res?.data || []);
            setPagination(res?.pagination || null);
        } catch (e) {
            console.error('Error loading pending reconciliation:', e);
            setData([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    };

    // Cargar datos al montar el componente
    useEffect(() => {
        fetchData(page, search);
    }, []); // Solo al montar

    // Manejar cambio de búsqueda
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        setPage(1);
        fetchData(1, value);
    };

    // Manejar cambio de página
    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchData(newPage, search);
    };

    // No mostrar nada si no hay datos
    const hasNoData = !loading && data.length === 0;

    return (
        <div className="card mt-6">
            <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icons.Link className="w-5 h-5 mr-2 text-blue-600" />
                    Pedidos pendientes por conciliar
                    {!hasNoData && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {pagination?.total || data.length}
                        </span>
                    )}
                </h2>
            </div>

            {/* Search Box */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                    <Icons.Search className="w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por pedido o cliente..."
                        value={search}
                        onChange={handleSearchChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {search && (
                        <button
                            onClick={() => {
                                setSearch('');
                                setPage(1);
                                fetchData(1, '');
                            }}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Limpiar búsqueda"
                        >
                            <Icons.X className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            <div className="card-content p-0">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">
                        <Icons.Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Cargando...
                    </div>
                ) : hasNoData ? (
                    <div className="p-8 text-center">
                        <Icons.Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium mb-1">
                            {search ? 'No se encontraron resultados' : 'No hay pedidos pendientes de conciliar'}
                        </p>
                        <p className="text-sm text-gray-500">
                            {search ? (
                                <>
                                    Intenta con otro término de búsqueda o{' '}
                                    <button
                                        onClick={() => {
                                            setSearch('');
                                            setPage(1);
                                            fetchData(1, '');
                                        }}
                                        className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                        limpia el filtro
                                    </button>
                                </>
                            ) : (
                                'Solo se muestran pedidos con balance > ±$2,000'
                            )}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Mobile View */}
                        <div className="sm:hidden space-y-3 p-3 bg-gray-50">
                            {data.map((order) => (
                                <div key={order.id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
                                    <div className="mb-2">
                                        <span className="text-sm font-bold text-gray-900 block">#{order.order_number}</span>
                                        <span className="text-xs text-gray-500 block">{new Date(order.created_at).toLocaleDateString('es-CO')}</span>
                                    </div>
                                    <div className="mb-2">
                                        <div className="text-xs font-semibold text-gray-700">Cliente</div>
                                        <div className="text-sm text-gray-900">{order.customer_name}</div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs border-t border-gray-100 pt-2 mt-2">
                                        <div>
                                            <span className="text-gray-500 block mb-0.5">Total</span>
                                            <span className="font-bold text-gray-800">${new Intl.NumberFormat('es-CO').format(order.total_amount)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-0.5">Pendiente</span>
                                            <span className="font-bold text-red-600">${new Intl.NumberFormat('es-CO').format(order.pending_amount || 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop View */}
                        <div className="!hidden sm:!block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pagado</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pendiente</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pago</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-bold text-gray-900">{order.order_number}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{order.customer_name}</td>
                                            <td className="px-4 py-3 text-sm font-bold text-gray-900">${new Intl.NumberFormat('es-CO').format(order.total_amount)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">${new Intl.NumberFormat('es-CO').format(order.paid_amount || 0)}</td>
                                            <td className="px-4 py-3 text-sm font-bold text-red-600">${new Intl.NumberFormat('es-CO').format(order.pending_amount || 0)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{order.payment_method}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString('es-CO')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {pagination && pagination.totalPages > 1 && (
                            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                                <span className="text-sm text-gray-600">
                                    Página {pagination.page} de {pagination.totalPages}
                                    {' '}({pagination.total} resultados)
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        disabled={pagination.page === 1}
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        disabled={pagination.page === pagination.totalPages}
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PendingReconciliationSection;
